import React, { useState, useMemo, useCallback } from "react";
import moment from "moment-timezone";
import Pagination from "../Pagination";

import withNavigate from "../withNavigate";
import { periodButtons, recordsData } from "../../../utils/constants";
import ChartWrapper from "../ChartWrapper";
import { DownArrowIcon, UpArrowIcon } from "../SvgIcon";
import SelectInput from "../SelectInput";
import comingSoonImg from "../../../icons/coming-soon.avif";


const RptAccountDeletions = (props) => {
  const [activeTab, setActiveTab] = useState("analytics");
  const [selectedPeriod, setSelectedPeriod] = useState("12M");
  const [selectedInterval, setSelectedInterval] = useState("Month");
  const [selectedChartType, setSelectedChartType] = useState("totals");
  const [chartViewMode, setChartViewMode] = useState("bar");
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);
  const [sort, setSort] = useState("");
  const [recordTypeFilter, setRecordTypeFilter] = useState("any");

  const metricsData = {
    totalDeletions: { value: 165, change: 148, percentage: 871 },
    clientDeletions: { value: 97, change: -86, percentage: 782 },
    driverDeletions: { value: 68, change: 62, percentage: 85 },
  };

  const handleInputChange = useCallback(
    (event) => {
      const { name, value, type, checked } = event.target;
      const inputValue = type === "checkbox" ? checked : value;

      switch (name) {
        case "search":
          setSearchValue(inputValue);
          break;
        case "record":
          setRecordTypeFilter(inputValue);
          setPage(1);
          break;
        default:
          break;
      }
    },
    [setSearchValue, setRecordTypeFilter, setPage],
  );

  // Generate dynamic chart data based on today's date and interval
  const generateChartData = (interval) => {
    const data = {};
    const reasons = [
      { key: "privacyConcerns", label: "Privacy Concerns", color: "#ef4444" },
      { key: "inactivity", label: "Inactivity", color: "#06b6d4" },
      { key: "foundAlternative", label: "Found Alternative", color: "#22c55e" },
      { key: "badExperience", label: "Bad Experience", color: "#ec4899" },
      { key: "priceIssues", label: "Price Issues", color: "#f97316" },
      { key: "other", label: "Other", color: "#94a3b8" },
    ];

    const userTypes = [
      { key: "client", label: "client", color: "#10b981" },
      { key: "driver", label: "driver", color: "#059669" },
    ];

    const generateDataPoint = (total) => ({
      total: total,
      client: Math.floor(total * 0.6),
      driver: Math.floor(total * 0.4),
      privacyConcerns: Math.floor(total * 0.15),
      inactivity: Math.floor(total * 0.1),
      foundAlternative: Math.floor(total * 0.1),
      badExperience: Math.floor(total * 0.25),
      priceIssues: Math.floor(total * 0.25),
      other: Math.ceil(total * 0.15),
    });

    // 7 Days - varies by interval
    data["7D"] = [];
    if (interval === "Day") {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i, "days");
        const totals = [3, 5, 2, 4, 6, 3, 2];
        const total = totals[6 - i];
        data["7D"].push({
          month: date.format("ddd D MMM"),
          ...generateDataPoint(total),
        });
      }
    } else {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i, "days");
        const totals = [3, 5, 2, 4, 6, 3, 2];
        const total = totals[6 - i];
        data["7D"].push({
          month: date.format("D MMM"),
          ...generateDataPoint(total),
        });
      }
    }

    // 30 Days
    data["30D"] = [];
    if (interval === "Day") {
      for (let i = 29; i >= 0; i--) {
        const date = moment().subtract(i, "days");
        const total = Math.floor(Math.random() * 5) + 2;
        data["30D"].push({
          month: date.format("D MMM"),
          ...generateDataPoint(total),
        });
      }
    } else {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i * 4, "days");
        const totals = [8, 12, 15, 18, 14, 16, 19];
        const total = totals[6 - i];
        data["30D"].push({
          month: date.format("D MMM"),
          ...generateDataPoint(total),
        });
      }
    }

    // 3 Months
    data["3M"] = [];
    if (interval === "Month") {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i, "months");
        const totals = [9, 12, 11, 26, 19, 7, 15];
        const total = totals[6 - i];
        data["3M"].push({
          month: date.format("MMM YYYY"),
          ...generateDataPoint(total),
        });
      }
    } else {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i, "months");
        const totals = [9, 12, 11, 26, 19, 7, 15];
        const total = totals[6 - i];
        data["3M"].push({
          month: date.format("MMM YYYY"),
          ...generateDataPoint(total),
        });
      }
    }

    // 6 Months
    data["6M"] = [];
    for (let i = 6; i >= 0; i--) {
      const date = moment().subtract(i, "months");
      const totals = [9, 10, 22, 9, 12, 11, 26];
      const total = totals[6 - i];
      data["6M"].push({
        month: date.format("MMM YYYY"),
        ...generateDataPoint(total),
      });
    }

    // 12 Months
    data["12M"] = [];
    if (interval === "Year") {
      for (let i = 2; i >= 0; i--) {
        const date = moment().subtract(i, "years");
        const total = Math.floor(Math.random() * 40) + 80;
        data["12M"].push({
          month: date.format("YYYY"),
          ...generateDataPoint(total),
        });
      }
    } else {
      for (let i = 11; i >= 0; i--) {
        const date = moment().subtract(i, "months");
        const totals = [18, 10, 12, 9, 10, 22, 9, 12, 11, 26, 19, 3];
        const total = totals[11 - i];
        data["12M"].push({
          month: date.format("MMM YYYY"),
          ...generateDataPoint(total),
        });
      }
    }

    return { data, reasons, userTypes };
  };

  const {
    data: chartData,
    reasons,
    userTypes,
  } = useMemo(() => generateChartData(selectedInterval), [selectedInterval]);

  // Interval options
  const intervalOptions = [
    { value: "Day", label: "Day" },
    { value: "Week", label: "Week" },
    { value: "Month", label: "Month" },
  ];

  // Chart type options
  const chartTypeOptions = [
    { value: "totals", label: "Totals" },
    { value: "clients", label: "By User Type" },
    { value: "drivers", label: "By Reason" },
  ];

  // Get current chart data based on selected period
  const getCurrentChartData = () => {
    return chartData[selectedPeriod] || chartData["12M"];
  };


  const renderRecordData = (record, index) => {
    return (
      <div className="c-list-entry align-items-center" key={index}>
        <div className="c-list-entry__location">
          <div className="text-capitalize">
            {record?.cancelledBy?.name || "-"}
            <br />
            <span className="text-sm text-gray-600 ">
              {record?.cancelledBy?.code}
            </span>
          </div>
        </div>

        <div className="c-list-entry__rating">
          {moment(record?.cancelledOn?.date)
            ?.tz("Europe/London")
            ?.format("DD MMM YYYY")}

          <br />
          <span className="text-sm text-gray-600 ">
            {moment(record?.cancelledOn?.date)
              ?.tz("Europe/London")
              ?.format("hh:mm ")}
          </span>
        </div>

        <div className="c-list-entry__location">{record.reason}</div>
        <div className="c-list-entry__location">{record.accountAge}</div>
        <button type="button" className="c-btn c-btn--teal c-btn--small">
          View Profile
        </button>
      </div>
    );
  };

  const renderAnalyticsTab = () => {
    return (
      <div className="booking-analytics">
        {/* Time Period Controls */}
        <div className="analytics-controls">
          <div className="period-buttons">
            {periodButtons?.map((button) => (
              <button
                key={button.id}
                className={`period-btn ${
                  selectedPeriod === button.id ? "active" : ""
                }`}
                onClick={() => setSelectedPeriod(button.id)}
              >
                {button.label}
              </button>
            ))}
          </div>

          <div className="interval-selector">
            <span className="interval-label">Interval:</span>
            <SelectInput
              value={intervalOptions.find(
                (opt) => opt.value === selectedInterval,
              )}
              onChange={(option) => setSelectedInterval(option.value)}
              options={intervalOptions}
              isSearchable={false}
              className="interval-select"
            />
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="metrics-cards">
          <div className="metric-card">
            <div className="metric-title">Total Deletions</div>
            <div className="d-flex align-items-center">
              <span className="metric-value me-5">
                {metricsData?.totalDeletions?.value}
              </span>
              <span
                className={`metric-change ${metricsData?.totalDeletions?.change >= 0 ? "positive" : "negative"}`}
              >
                {metricsData?.totalDeletions?.change >= 0 ? (
                  <UpArrowIcon />
                ) : (
                  <DownArrowIcon />
                )}
                {metricsData?.totalDeletions?.change >= 0 ? "+" : "-"}
                {Math.abs(metricsData?.totalDeletions?.change)} (
                {metricsData?.totalDeletions?.percentage}%)
              </span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Client Deletions</div>
            <div className="d-flex align-items-center">
              <span className="metric-value me-5">
                {metricsData?.clientDeletions?.value}
              </span>
              <span
                className={`metric-change ${metricsData?.clientDeletions?.change >= 0 ? "positive" : "negative"}`}
              >
                {metricsData?.clientDeletions?.change >= 0 ? (
                  <UpArrowIcon />
                ) : (
                  <DownArrowIcon />
                )}
                {metricsData?.clientDeletions?.change >= 0 ? "+" : "-"}
                {Math.abs(metricsData?.clientDeletions?.change)} (
                {metricsData?.clientDeletions?.percentage}%)
              </span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Driver Deletions</div>
            <div className="d-flex align-items-center">
              <span className="metric-value me-5">
                {metricsData?.driverDeletions?.value}
              </span>
              <span
                className={`metric-change ${metricsData?.driverDeletions?.change >= 0 ? "positive" : "negative"}`}
              >
                {metricsData?.driverDeletions?.change >= 0 ? (
                  <UpArrowIcon />
                ) : (
                  <DownArrowIcon />
                )}
                {metricsData?.driverDeletions?.change >= 0 ? "+" : "-"}
                {Math.abs(metricsData?.driverDeletions?.change)} (
                {metricsData?.driverDeletions?.percentage}%)
              </span>
            </div>
          </div>
        </div>

        {/* Chart Section */}
        <div className="chart-section">
          <ChartWrapper
            data={getCurrentChartData()}
            chartTypeOptions={chartTypeOptions}
            selectedChartType={selectedChartType}
            onChartTypeChange={setSelectedChartType}
            chartViewMode={chartViewMode}
            onChartViewModeChange={setChartViewMode}
            dataKey="month"
            height={350}
            series={
              selectedChartType === "drivers"
                ? reasons
                : selectedChartType === "clients"
                  ? userTypes
                  : null
            }
            valueKey="total"
          />
        </div>
      </div>
    );
  };

  const sortRecords = (event, field) => {
    if (event) event.preventDefault();

    const sortFields = {
      firstname: { asc: "firstname=1", desc: "firstname=-1" },
      amountSorter: { asc: "amountSorter=1", desc: "amountSorter=-1" },
      statusSorter: { asc: "statusSorter=1", desc: "statusSorter=-1" },
      accountAge: { asc: "accountAge=1", desc: "accountAge=-1" },
    };

    const currentSort = sortFields[field];
    if (!currentSort) return;

    const newSort =
      sort === currentSort.asc ? currentSort.desc : currentSort.asc;

    setSearch("");
    setSort(newSort);
    setPage(1);
  };
  const getSortClass = (key) => {
    if (sort === `${key}=1`) {
      return "c-list-filter__button c-list-filter__button--icon-up";
    } else if (sort === `${key}=-1`) {
      return "c-list-filter__button c-list-filter__button--icon-down";
    }
    return "c-list-filter__button c-list-filter__button--faded";
  };

  const SortButton = useCallback(
    ({ field, label }) => (
      <button
        className={getSortClass(field)}
        onClick={(e) => sortRecords(e, field)}
      >
        {label}
      </button>
    ),
    [getSortClass, sortRecords],
  );

  const ColumnHeaders = React.memo(({ sort, onSort }) => (
    <div>
      <div className="c-list-entry c-list-entry__header">
        <div className="c-list-entry__location c-list-filter">
          <SortButton field="firstname" label="Deleted Account" />
        </div>
        <div className="c-list-entry__rating c-list-filter">
          <SortButton field="statusSorter" label="Deleted On" />
        </div>
        <div className="c-list-entry__location c-list-filter">
          <SortButton field="amountSorter" label="Reason" />
        </div>
        <div className="c-list-entry__location c-list-filter">
          <SortButton field="accountAge" label="Account Age" />
        </div>
      </div>
    </div>
  ));

  const searchRecords = () => {
    setSearch(searchValue);
    setRecordTypeFilter("all");
    setPage(1);
  };
  const renderRecordsTab = () => {
    return (
      <div>
        <div className="">
          <div className="c-subheader-info-panel">
            <div className="c-subheader-info-panel__search">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  searchRecords();
                }}
              >
                <input
                  type="text"
                  value={searchValue || ""}
                  onChange={handleInputChange}
                  name="search"
                  placeholder="Search records..."
                  className="c-subheader-info-panel__input"
                />
                <button
                  type="button"
                  className="c-btn-outline c-btn-outline--white c-btn-outline--small"
                  onClick={() => searchValue !== null && searchRecords()}
                >
                  Search
                </button>
              </form>
            </div>
          </div>

          <div className="c-hr"></div>

          <div className="l-row">
            <div className="l-col-100 u-padding-top-0 u-padding-bottom-0">
              <div className="filter-bar">
                {[
                  {
                    id: "any",
                    label: "Any",
                  },
                  {
                    id: "client",
                    label: "Client",
                  },
                  {
                    id: "mav",
                    label: "Mav",
                  },
                ]?.map(({ id, label }) => (
                  <div className="c-checkbox-radio-block" key={id}>
                    <input
                      type="radio"
                      id={id}
                      name="record"
                      value={id}
                      onChange={handleInputChange}
                      checked={recordTypeFilter === id}
                    />
                    <label htmlFor={id}>{label}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="l-col-100">
              <div className="c-hr u-margin-bottom-0"></div>
              <ColumnHeaders />

              <div>
                {recordsData?.length > 0 ? (
                  recordsData?.map(renderRecordData)
                ) : (
                  <div className="u-text-align-center u-margin-top-bottom-m u-color-red">
                    No records found
                  </div>
                )}
              </div>

              <div className="c-hr u-margin-bottom-0 u-clear-both"></div>

              <Pagination
                pageSize={pageSize}
                totalPages={totalPages}
                page={page}
                setPage={setPage}
                inputValue={inputValue}
                setInputValue={setInputValue}
                setPageSize={setPageSize}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="l-base-layout__content">
      <h1>Account deletions</h1>
      <div className="c-hr"></div>
      {/* <div className="custom-tabs">
        <div
          className={`tab-item ${activeTab === "analytics" ? "active" : ""}`}
          onClick={() => setActiveTab("analytics")}
        >
          Analytics
        </div>
        <div
          className={`tab-item ${activeTab === "records" ? "active" : ""}`}
          onClick={() => setActiveTab("records")}
        >
          Records
        </div>
      </div> */}

      <div>
        {/* {activeTab === "analytics" ? renderAnalyticsTab() : renderRecordsTab()} */}

   <div className="coming-soon-img-section">
          <img src={comingSoonImg} alt="Coming-Soon" width={550} />
        </div>      </div>
    </div>
  );
};

export default withNavigate(RptAccountDeletions);
